import * as React from "react"
import compose from "recompose/compose"
import injectSheet from "react-jss"
import { withi18n } from "@/helpers/hoc/withi18n"
import { DropdownInput, Menu, Text } from "@xingternal/360-components"
import { DatePicker } from "@/components"
import { DateRangePickerTrigger, DateRangeInputs, BenchmarkSettings } from ".."
import { useIsRebrandingEnabled } from "@xingternal/rebranding-adapter"

const styles = {
  dateRangeDropdown: { composes: "z3", minWidth: 260 },
  dateRangeDropdownContent: {
    composes: "flex",
  },
  datePicker: { composes: "p16" },
  sidebar: { composes: "p16 border-left border-borders-grey" },
  label: { composes: "pb8" },
  dateSetDropdown: { width: 260, height: 32 },
  benchmarkSettings: { composes: "mt16" },
}

const i18n = {
  dateRangeLabel: "360_RECRUITER_INSIGHTS_DATE_RANGE",
  custom: "360_RECRUITER_INSIGHTS_DATE_RANGE_CUSTOM",
  lastNumberOfDays: () => "360_RECRUITER_INSIGHTS_OVERVIEW_LAST_DAYS",
}

const DateRangePickerView = ({
  benchmarkSettings,
  classes,
  dateRange,
  disabledDays,
  modifiers,
  numberOfMonths,
  onDayClick,
  onDateRangeChange,
  onInputDateFocus,
  dateRangeLabel,
  custom,
  lastNumberOfDays,
  i18n,
}) => {
  const { enableXingRebranding } = useIsRebrandingEnabled()

  const predefinedRanges = [
    {
      id: "last7days",
      label: lastNumberOfDays({ days: 7 }),
      onClick() {
        onDateRangeChange("7")
      },
      active: dateRange.lastDaysValue === "7",
      data: { testid: "last7days" },
    },
    {
      id: "last30days",
      label: lastNumberOfDays({ days: 30 }),
      onClick() {
        onDateRangeChange("30")
      },
      active: dateRange.lastDaysValue === "30",
      data: { testid: "last30days" },
    },
    {
      id: "last60days",
      label: lastNumberOfDays({ days: 60 }),
      onClick() {
        onDateRangeChange("60")
      },
      active: dateRange.lastDaysValue === "60",
      data: { testid: "last60days" },
    },
    {
      id: "last90days",
      label: lastNumberOfDays({ days: 90 }),
      onClick() {
        onDateRangeChange("90")
      },
      active: dateRange.lastDaysValue === "90",
      data: { testid: "last90days" },
    },
    {
      id: "custom",
      label: custom,
      onClick() {
        onDateRangeChange("custom")
      },
      active: dateRange.lastDaysValue === "custom",
      data: { testid: "custom" },
    },
  ]

  return (
    <div>
      <DropdownInput
        right
        filled
        closeOnContentClick={false}
        className={classes.dateRangeDropdown}
        content={
          <div
            className={classes.dateRangeDropdownContent}
            data-testid="dateRangeDropdownContent"
          >
            <DatePicker
              className={classes.datePicker}
              disabledDays={disabledDays}
              locale={i18n.language}
              modifiers={modifiers}
              numberOfMonths={numberOfMonths}
              onDayClick={onDayClick}
              selectedDays={[
                dateRange.from,
                { from: dateRange.from, to: dateRange.to },
              ]}
              toMonth={new Date()}
            />
            <div className={classes.sidebar}>
              <Text className={classes.label} size={13} bold>
                {dateRangeLabel}
              </Text>
              <DropdownInput
                filled
                content={
                  <Menu
                    items={predefinedRanges}
                    id={enableXingRebranding && "date-range-xing-rebranded"}
                  />
                }
                triggerValue={
                  dateRange.lastDaysValue === "custom"
                    ? custom
                    : dateRange.triggerText
                }
              />

              <DateRangeInputs
                dateRange={dateRange}
                onInputDateFocus={onInputDateFocus}
              />
              <BenchmarkSettings className={classes.benchmarkSettings} />
            </div>
          </div>
        }
        triggerValue={
          <DateRangePickerTrigger
            rangeText={dateRange.triggerText}
            benchmarkSettings={benchmarkSettings}
          />
        }
      />
    </div>
  )
}

const enhance = compose(withi18n(i18n), injectSheet(styles))

export const EnhancedDateRangePickerView = enhance(DateRangePickerView)
export default DateRangePickerView
